import { FC } from "react";
import Stack from "@mui/material/Stack";
import { QuestionType } from "@simplyk/common";
import { useController, useFormContext } from "react-hook-form";

import { QuestionField } from "@/canvas/features/FormV2/blocks/questions/QuestionField/QuestionField";
import { isMemberIdentityQuestion } from "@/canvas/features/FormV2/helpers/membershipMemberInfo";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput } from "@/common/types/trpc";
import { FormRadioSelectGroup } from "@/forms-design-system/FormRadioSelectGroup";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { FormButton } from "@/forms-design-system/FormButton";
import { Delete } from "@/icons/outlined";

export interface MemberInfoBlockProps {
  label?: string;
  onRemove?: () => void;
  requireIdentity?: boolean;
  participantIndex: number;
  memberNumber: number;
  memberCount: number;
  questions: QuestionOutput[];
  offersBuyerChoice: boolean;
  isForBuyerDefault: boolean | undefined;
  fieldIdPrefix: string;
  isEditingQuestions?: boolean;
}

export const MemberInfoBlock: FC<MemberInfoBlockProps> = ({
  label,
  onRemove,
  requireIdentity,
  participantIndex,
  memberNumber,
  memberCount,
  questions,
  offersBuyerChoice,
  isForBuyerDefault,
  fieldIdPrefix,
  isEditingQuestions = false,
}) => {
  const { t } = useTranslate();
  const form = useFormContext<TicketingPaymentInput>();

  const isForBuyerName = `customAnswersOfParticipants.${participantIndex}.isForBuyer` as const;
  const { field: isForBuyerField } = useController({
    control: form.control,
    name: isForBuyerName,
    defaultValue: isForBuyerDefault,
  });
  const hidesIdentityQuestions = !isEditingQuestions && offersBuyerChoice && (isForBuyerField.value ?? true);
  const visibleQuestions = questions.flatMap((question, answerIndex) =>
    hidesIdentityQuestions && isMemberIdentityQuestion(question) ? [] : [{ question, answerIndex }]
  );

  const clearIdentityAnswers = () => {
    questions.forEach((question, answerIndex) => {
      if (!isMemberIdentityQuestion(question)) {
        return;
      }
      const answerName = `customAnswersOfParticipants.${participantIndex}.answers.${answerIndex}.answers` as const;
      form.setValue(answerName, []);
      form.clearErrors(answerName);
    });
  };

  return (
    <Stack data-test="member-info-block" gap={1} sx={{ pt: requireIdentity && memberNumber > 1 ? 1 : 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTypography variant="subtitle2" vibe="supershy" color={requireIdentity ? "#9C9CBF" : undefined}>
          {label ??
            (memberCount > 1
              ? `${t("ticketing", "memberInfo.member")} #${memberNumber}`
              : t("ticketing", "memberInfo"))}
        </FormTypography>
        {onRemove && (
          <FormButton
            iconButton
            size="small"
            variant="ghost"
            onClick={onRemove}
            sx={(theme) => ({ my: -0.625, mr: 0.125, color: theme.palette.text.neutral.quiet })}
            aria-label={t("ticketing", "groupMembership.removeMember", { count: memberNumber })}
            startIcon={<Delete />}
          />
        )}
      </Stack>

      <Stack gap={requireIdentity ? 1 : 2}>
        {offersBuyerChoice && (
          <Stack sx={{ py: 0.5 }}>
            <FormRadioSelectGroup
              name={isForBuyerName}
              layout="vertical"
              options={[
                { label: t("ticketing", "memberInfo.forMe"), value: true },
                { label: t("ticketing", "memberInfo.forSomeoneElse"), value: false },
              ]}
              value={isEditingQuestions ? false : (isForBuyerField.value ?? true)}
              onChange={(value) => {
                isForBuyerField.onChange(value);
                if (value) {
                  clearIdentityAnswers();
                }
              }}
            />
          </Stack>
        )}

        {visibleQuestions.length > 0 && (
          <Stack gap={2}>
            {visibleQuestions.map(({ question, answerIndex }) => (
              <QuestionField
                nameFieldSpacing={requireIdentity ? 2 : undefined}
                sx={
                  requireIdentity &&
                  [QuestionType.Name, QuestionType.Email, QuestionType.Multiple].includes(question.type)
                    ? (theme) => ({
                        "& .MuiInputBase-root": { height: theme.spacing(4.5), boxSizing: "border-box" },
                        "& .MuiInputBase-input:not(.MuiSelect-select)": { minHeight: theme.spacing(3.25) },
                      })
                    : undefined
                }
                key={question.id}
                name={`customAnswersOfParticipants.${participantIndex}.answers.${answerIndex}`}
                question={
                  requireIdentity && isMemberIdentityQuestion(question)
                    ? { ...question, required: question.type === QuestionType.Name }
                    : question
                }
                id={`${fieldIdPrefix}-group-0-participant-${memberNumber - 1}-question-${answerIndex}`}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
